import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Check as ApproveIcon, Send as PostIcon, Undo as ReverseIcon } from '@mui/icons-material';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerFormDialog from '../LedgerFormDialog';
import { InventoryStatusChip, getErrorMessage } from './InventoryShared';

// Result + workflow dialog for write-only inventory documents. The backend
// exposes create/approve/post/reverse but NO list or detail endpoints, so
// after creating a document this dialog shows its number and offers the
// next lifecycle step inline (DRAFT -> APPROVED -> POSTED, or reverse a
// POSTED document with a reason). `extraContent` renders document-specific
// follow-ups (e.g. link-AP-invoice on receipts).
const DocResultDialog = ({
  open,
  onClose,
  title,
  doc,
  setDoc,
  numberOf,
  statusOf,
  api,
  canApprove,
  renderSummary,
  extraContent,
}) => {
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();
  const [reverseDlg, setReverseDlg] = useState({ open: false, reason: '' });
  const [busy, setBusy] = useState(false);

  if (!doc) return null;
  const status = String(statusOf(doc) || 'DRAFT').toUpperCase();
  const id = doc.id;

  const runStep = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} ${title.toLowerCase()} ${numberOf(doc)}?`,
      onConfirm: async () => {
        try {
          setBusy(true);
          const res = await fn();
          const updated = res?.data?.data ?? res?.data ?? null;
          if (updated && typeof updated === 'object') setDoc({ ...doc, ...updated, id });
          showSnackbar(`${title} ${pastTense} successfully`, 'success');
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        } finally {
          setBusy(false);
        }
      },
    });
  };

  const submitReverse = async () => {
    if ((reverseDlg.reason?.trim() ?? '').length < 5) {
      showSnackbar('Reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      setBusy(true);
      const res = await api.reverse(id, { reason: reverseDlg.reason.trim() });
      const updated = res?.data?.data ?? res?.data ?? null;
      if (updated && typeof updated === 'object') setDoc({ ...doc, ...updated, id });
      showSnackbar(`${title} reversed successfully`, 'success');
      setReverseDlg({ open: false, reason: '' });
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{title} {numberOf(doc)} created</DialogTitle>
        <DialogContent>
          {renderSummary?.(doc)}
          <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
            <InventoryStatusChip status={status} />
          </Box>
          {extraContent?.(doc, showSnackbar)}
          <Box display="flex" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
            {status === 'DRAFT' && canApprove && (
              <Button size="small" variant="outlined" startIcon={<ApproveIcon />} disabled={busy} onClick={() => runStep('Approve', 'approved', () => api.approve(id))}>
                Approve
              </Button>
            )}
            {status === 'APPROVED' && canApprove && (
              <Button size="small" variant="contained" startIcon={<PostIcon />} disabled={busy} onClick={() => runStep('Post', 'posted', () => api.post(id))}>
                Post
              </Button>
            )}
            {status === 'POSTED' && canApprove && (
              <Button size="small" variant="outlined" color="error" startIcon={<ReverseIcon />} disabled={busy} onClick={() => setReverseDlg({ open: true, reason: '' })}>
                Reverse
              </Button>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
            Post the document to update stock and the GL. Track posted movements under Reports -&gt; Movement.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Done</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={reverseDlg.open}
        onClose={() => setReverseDlg({ open: false, reason: '' })}
        title={`Reverse ${title.toLowerCase()} ${numberOf(doc)}`}
        onSubmit={submitReverse}
        values={reverseDlg}
        onChange={(key, value) => setReverseDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'reason', label: 'Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default DocResultDialog;
